import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { DateTime } from "luxon";
import { FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist/es/constants";
import storage from 'redux-persist/es/storage';
import { persistReducer, persistStore } from 'redux-persist';
import storageSession from 'redux-persist/es/storage/session';

import { ajaxController } from "../ajax/controller/AjaxController";
import { refreshToken } from "./actions/authActions";
import selectedShiftReducer, { setSelectedDate, setSelectedShift } from './reducers/shiftReducer';
import authReducer from './reducers/authReducer';
import viewReducer from './reducers/viewReducer';
import themeReducer from './reducers/themeReducer';

const viewPersistConfig = {
  key: 'view',
  storage: storageSession, // Note: This is per tab
};

const rootReducer = combineReducers({
  authReducer: authReducer,
  shift: selectedShiftReducer,
  view: persistReducer(viewPersistConfig, viewReducer),
  theme: themeReducer
});

const rootPersistConfig = {
  key: 'root',
  storage,
  whitelist: ['authReducer'],
  blacklist: ['view'],
};

const persistedReducer = persistReducer(rootPersistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export default store;

// hydrate view slice from sessionStorage if present and redux has defaults
const currentShiftState = store.getState().shift
const storedDate = sessionStorage.getItem("shiftDate");
const storedShift = sessionStorage.getItem("shiftDayOrNight");

// only override if redux has its initial defaults (to avoid resetting when navigating back)
if (storedDate && currentShiftState.selectedDate === DateTime.now().toISODate()) {
  store.dispatch(setSelectedDate(storedDate));
}
if (storedShift && currentShiftState.selectedShift === 'DAY') {
  const shiftCode = storedShift === "D" ? "DAY" : "NIGHT";
  store.dispatch(setSelectedShift(shiftCode));
}

// subscribe to keep sessionStorage in sync when view.selectedDate / selectedShift change
let prevShiftState = store.getState().shift;
store.subscribe(() => {
  const nextShiftState = store.getState().shift;
  const dateChanged = nextShiftState.selectedDate !== prevShiftState.selectedDate;
  const shiftChanged = nextShiftState.selectedShift !== prevShiftState.selectedShift;

  if (dateChanged || shiftChanged) {
    sessionStorage.setItem("shiftDate", nextShiftState.selectedDate);
    const backendCode = nextShiftState.selectedShift === "DAY" ? "D" : "N";
    sessionStorage.setItem("shiftDayOrNight", backendCode);
  }
  prevShiftState = nextShiftState;
});

// setup ajax controller to get token from redux store
ajaxController.setTokenGetter(() => {
  const state = store.getState();
  return state.authReducer;
});

ajaxController.setTokenRefreshCallback(() =>
  store.dispatch<any>(refreshToken(store.getState().authReducer.refreshToken!))
);